import React, { useState, useEffect, useRef } from "react"
import withStyles from "react-jss"
import { DishesTooltip, Link } from ".."
import {
  BREAKPOINT,
  TRANSITION_DURATION,
  CONTAINER_STYLE,
} from "../../data/globalStyles"
import paths from "../../data/paths"
import regionMapping, {
  regions,
  regionCenter,
  regionIdMapping,
} from "../../data/regions"
import recipes from "../../data/recipes"
import colors from "../../data/colors"
import { isIOS } from "react-device-detect"
import { getScrollPosition, getKeyByValue } from "../../utils/helper"
// import { preloadImages } from "../../utils/preloadImages"

const styles = {
  mapContainer: {
    position: "relative",
    ...CONTAINER_STYLE,
  },
  svg: {
    width: "100%",
    height: "100%",
    maxHeight: "90vh",
  },
  polygon: {
    transition: `opacity ${TRANSITION_DURATION}s ease-in-out`,
  },
  hiddenTextContainer: {
    textAlign: "center",
    pointerEvents: "none",
    opacity: 0,
  },
  hiddenText: {
    pointerEvents: "none",
    opacity: 0,
  },
  mobileContent:
    // CONTAINER_STYLE,
    {
      width: "100%",
      maxWidth: "650px",
    },
  mobileRegionIdContainer: {
    transform: "translate(-28px, -46px)",
    pointerEvents: "none",
    transition: `opacity ${TRANSITION_DURATION}s ease-in-out`,
    willChange: "opacity",
  },
  mobileRegionId: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    borderRadius: "50%",
    lineHeight: 1.5,
    backgroundColor: colors.white,
    color: colors.black,
    fontFamily: "Jost",
    fontWeight: 600,
    fontSize: "36px",
    pointerEvents: "none",
  },
  regionsLabelContainer: {
    display: "flex",
    flexFlow: "column wrap",
    alignItems: "center",
    width: "100%",
    maxHeight: "20ch",
    margin: "1rem 1.5rem 3rem 1.5rem",
    [`@media only screen and (max-width: ${BREAKPOINT}px)`]: {
      margin: "1rem 0",
    },
    [`@media only screen and (max-width: 370px)`]: {
      maxHeight: "20ch",
    },
  },
  regionContainer: {
    outline: "none",
  },
  region: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    cursor: "pointer",
    width: "50%",
    height: "5ch",
    transition: `opacity ${TRANSITION_DURATION}s ease-in-out`,
    [`@media only screen and (max-width: 370px)`]: {
      height: "5ch",
    },
  },
  regionName: {
    textAlign: "left",
    padding: "0 1rem 0 0",
    lineHeight: 1.2,
    fontWeight: "bold",
    fontSize: "calc(16px + (18 - 16) * ((100vw - 300px) / (1600 - 300)))",
    textTransform: "uppercase",
    [`@media only screen and (max-width: 370px)`]: {
      fontSize: "15px",
    },
  },
  recipesListMobile: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    flexDirection: "column",
  },
  recipe: {},
  imgContainer: {
    aspectRatio: "auto 16 / 9",
    margin: 0,
  },
  recipeImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  recipeName: {
    margin: "0.5rem 0 1.5rem 0",
    lineHeight: 1.2,
    color: "black",
    fontWeight: 400,
    fontSize: "calc(18px + (20 - 18) * ((100vw - 300px) / (1600 - 300)))",
  },
  toTopContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  toTop: {
    textAlign: "center!important",
    fontSize:
      "calc(16px + (28 - 16) * ((100vw - 300px) / (1600 - 300)))!important",
    fontWeight: `${400}!important`,
    color: `${colors.black}!important`,
    cursor: "pointer",
  },
  attributionContainer: {
    ...CONTAINER_STYLE,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  url: {
    color: "rgb(5, 113, 184)!important",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  attribution: {
    textAlign: "center",
    fontSize:
      "calc(12px + (14 - 12) * ((100vw - 300px) / (1600 - 300)))!important",
    color: `${colors.black}!important`,
    fontWeight: `${400}!important`,
  },
}

const Map = ({ classes, mobileView, screenSize }) => {
  const refs = useRef([])
  const mapContainerRef = useRef(null)
  const mobileRecipesListRef = useRef(null)

  const NO_ACTIVE_REGION = "no-active-region"
  const [activeRegion, setActiveRegion] = useState(NO_ACTIVE_REGION)
  const [dishes, setDishes] = useState([])

  // useEffect(() => {
  //   const images = Object.values(recipes)
  //     .flatMap((region) => region.flatMap((d) => [d.img, d.recipeImg]))
  //     .filter((src) => src)
  //   preloadImages(images)
  // }, [])

  useEffect(() => {
    if (activeRegion && Object.keys(recipes).includes(activeRegion)) {
      setDishes(recipes[activeRegion])
    } else {
      setDishes([])
    }
  }, [activeRegion])

  const getOpacity = (d) => {
    if (activeRegion === NO_ACTIVE_REGION) return 1
    else if (activeRegion === d.region) return 1
    else return 0.2
  }

  const getStrokeColor = (d) => {
    // if (activeRegion === d.region) return colors.regionHover
    return colors.white
  }

  const fillColor = ({ region }) => {
    if (region === regionMapping.region1) return colors.region1
    else if (region === regionMapping.region2) return colors.region2
    else if (region === regionMapping.region3) return colors.region3
    else if (region === regionMapping.region4) return colors.region4
    else if (region === regionMapping.region5) return colors.region5
    else if (region === regionMapping.region6) return colors.region6
    else if (region === regionMapping.region7) return colors.region7
    else if (region === regionMapping.region8) return colors.region8
  }

  const handleMouseEnter = (region) => {
    setActiveRegion(region)
  }

  const removeActiveRegion = () => {
    if (activeRegion === NO_ACTIVE_REGION) return
    setActiveRegion(NO_ACTIVE_REGION)
  }

  const keyHandler = ({ code }, region) => {
    if (code === "Enter") {
      if (activeRegion === NO_ACTIVE_REGION) {
        handleMouseEnter(region)
        return
      }
      removeActiveRegion()
    }
  }

  const handleMobileClick = (d) => {
    let region = d
    if (d.region) {
      region = d.region
    }
    if (activeRegion === region) {
      setActiveRegion(NO_ACTIVE_REGION)
      return
    }
    setActiveRegion(region)
    if (mobileRecipesListRef.current) {
      window.scrollTo({
        behavior: "smooth",
        top: getScrollPosition(mobileRecipesListRef),
      })
    }
  }

  const addRef = (el) => {
    if (el && !refs.current.includes(el)) {
      refs.current.push(el)
    }
  }

  const scrollToMap = () => {
    if (mapContainerRef && mapContainerRef.current) {
      window.scrollTo({
        behavior: "smooth",
        top: getScrollPosition(mapContainerRef),
      })
    }
  }

  const [tooltipsPosition, setTooltipsPosition] = useState([])

  useEffect(() => {
    if (refs.current.length && mapContainerRef.current) {
      // On larger screens, since mapContainer is restricted to 1000px
      // and are horizontally centered, the tooltips end up having a
      // large left padding. Subtracting the left and top position of
      // mapContainer with the tooltips will do the trick
      const { left: leftAdjustment, top: topAdjustment } =
        mapContainerRef.current.getBoundingClientRect()
      const EPI_HEADER_HEIGHT = 0
      const hoverPosition = refs.current.map((ref) => {
        const { left, top, width, height } = ref.getBoundingClientRect()
        const center = [
          left + width / 2 - leftAdjustment,
          top + height / 2 - topAdjustment + EPI_HEADER_HEIGHT,
        ]
        const region = ref.textContent
        return {
          region,
          center,
        }
      })
      setTooltipsPosition(hoverPosition)
    }
  }, [screenSize.width, screenSize.height])

  return (
    <>
      <div className={classes.mapContainer} ref={mapContainerRef}>
        <svg
          className={classes.svg}
          viewBox="0 0 1000 850"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill={colors.white}
            {...(!mobileView && { onMouseEnter: removeActiveRegion })}
            {...(mobileView && { onClick: removeActiveRegion })}
          />
          {Object.values(regionMapping).map((region, i) => {
            const areas = paths.filter((d) => d.region === region)
            const gTabIndex =
              activeRegion === region || activeRegion === NO_ACTIVE_REGION
                ? 0
                : -1
            return (
              <g
                key={i}
                className={classes.regionContainer}
                tabIndex={gTabIndex}
                {...(!mobileView && {
                  onKeyDown: (e) => keyHandler(e, region),
                })}
                aria-label={region}>
                {areas.map((d, i) => (
                  <polygon
                    key={i}
                    aria-label={d.name}
                    className={classes.polygon}
                    points={d.d}
                    fill={fillColor(d)}
                    stroke={getStrokeColor(d)}
                    strokeWidth="0.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    {...(!mobileView && {
                      onMouseEnter: () => handleMouseEnter(region),
                    })}
                    {...(mobileView && {
                      onClick: () => handleMobileClick(d),
                    })}
                    style={{
                      opacity: getOpacity(d),
                    }}
                  />
                ))}
              </g>
            )
          })}
          <g>
            {regions.map((d, i) => (
              <foreignObject
                ref={addRef}
                key={i}
                className={classes.hiddenTextContainer}
                x={regionCenter[i][0]}
                y={regionCenter[i][1]}
                width="1px"
                height="1px"
                textAnchor="middle"
                dominantBaseline="middle">
                <div
                  className={classes.hiddenText}
                  xmlns="http://www.w3.org/1999/xhtml">
                  {d}
                </div>
              </foreignObject>
            ))}
          </g>
          <g>
            {mobileView
              ? regions.map((d, i) => (
                  <foreignObject
                    key={i}
                    className={classes.mobileRegionIdContainer}
                    x={regionCenter[i][0]}
                    y={regionCenter[i][1]}
                    width="60px"
                    height="100%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{
                      opacity: getOpacity({ region: d }),
                    }}>
                    <div
                      className={classes.mobileRegionId}
                      xmlns="http://www.w3.org/1999/xhtml">
                      {regionIdMapping[d]}
                    </div>
                  </foreignObject>
                ))
              : null}
          </g>
        </svg>
        {!mobileView && !isIOS
          ? tooltipsPosition.map((d, i) => (
              <DishesTooltip key={i} data={d} activeRegion={activeRegion} />
            ))
          : null}
      </div>
      {mobileView ? (
        <div className={classes.mobileContent}>
          <div className={classes.regionsLabelContainer}>
            {Object.keys(regionIdMapping).map((region) => {
              const id = regionIdMapping[region]
              const colorKey = getKeyByValue(regionMapping, region)
              return (
                <div
                  key={id}
                  className={classes.region}
                  style={{
                    opacity: getOpacity({ region }),
                  }}
                  onClick={() => handleMobileClick(region)}>
                  <div className={classes.regionName}>{id}</div>
                  <div
                    key={id}
                    className={classes.regionName}
                    style={{
                      color: colors[colorKey],
                    }}>
                    {region}
                  </div>
                </div>
              )
            })}
          </div>
          <div className={classes.recipesListMobile} ref={mobileRecipesListRef}>
            {dishes.length ? (
              <>
                {dishes.map((dish, i) => (
                  <div className={classes.recipe} key={i}>
                    <Link href={dish.url}>
                      <div className={classes.imgContainer}>
                        <img
                          className={classes.recipeImg}
                          src={dish.recipeImg}
                          alt={dish.name}
                        />
                      </div>
                      <div className={classes.recipeName}>{dish.name}</div>
                    </Link>
                  </div>
                ))}
                <div className={classes.toTopContainer}>
                  <p className={classes.toTop} onClick={scrollToMap}>
                    Back to top
                  </p>
                </div>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className={classes.attributionContainer}>
        <p className={classes.attribution}>
          Built by{" "}
          <a
            className={classes.url}
            href="https://datavisualization.condenast.com/"
            target="_blank"
            rel="noreferrer">
            Condé Nast Data Story Lab
          </a>
        </p>
      </div>
    </>
  )
}

export default withStyles(styles)(Map)
